
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HeaderProfileIcon from '../../icons/HeaderProfileIcon';
import NotificationIcon from '../../icons/NotificationIcon';
import NotificationsPanel from '../../components/NotificationsPanel';
import { rememberOAuthProvider } from '../../utils/oauth';

const DesktopHeader = () => {
  const { isAuthenticated, user, login, logout, error, clearError, register } = useAuth();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [loginForm, setLoginForm] = useState({ identifier: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    name: '',
    accountName: '',
    email: '',
    password: ''
  });
  const [localError, setLocalError] = useState('');


  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);


  const [unreadCount, setUnreadCount] = useState(0);


  const [openNotif, setOpenNotif] = useState(false);

  const FN_BASE =
    (typeof import.meta !== 'undefined'
      ? import.meta.env?.VITE_FN_BASE
      : process.env?.REACT_APP_FN_BASE) || '/auth';
  const AUTH_BASE = String(FN_BASE || '').replace(/\/$/, '');


  const API_BASE =
    (typeof import.meta !== 'undefined'
      ? import.meta.env?.VITE_API_BASE
      : process.env?.REACT_APP_API_BASE) || '/.netlify/functions/api';

  const NOTIFICATIONS_UNREAD_URL = `${String(API_BASE || '').replace(
    /\/$/,
    ''
  )}/notifications/unread-count`;

  const openCreateAccount = () => {
    setModalType('create');
    setIsModalOpen(true);
    clearError();
    setLocalError('');
  };

  const openLogin = () => {
    setModalType('login');
    setIsModalOpen(true);
    clearError();
    setLocalError('');
    setLoginForm({ identifier: '', password: '' });
  };

  const openForgotPassword = () => {
    setModalType('forgot');
    setIsModalOpen(true);
    clearError();
    setLocalError('');
    setForgotEmail('');
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalType('');
    clearError();
    setLocalError('');
    setForgotEmail('');
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(false);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const ok = await login(
        String(loginForm.identifier || '').trim(),
        loginForm.password
      );
      if (ok) closeModal();
    } catch (err) {
      setLocalError('تعذر تسجيل الدخول');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
    if (error) clearError();
    setLocalError('');
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterForm((prev) => ({ ...prev, [name]: value }));
    if (error) clearError();
    setLocalError('');
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    const email = String(registerForm.email || '').trim();
    const password = String(registerForm.password || '');
    const username = String(registerForm.accountName || '').trim();
    const displayName = String((registerForm.name || '').trim());

    if (!username || !email || !password) {
      setLocalError('اسم الحساب، البريد وكلمة المرور حقول مطلوبة');
      return;
    }
    if (!/^[a-zA-Z0-9._-]{5,30}$/.test(username)) {
      setLocalError(
        'اسم الحساب يجب أن يكون 5–30 حروف/أرقام ويمكن أن يحتوي . _ - فقط'
      );
      return;
    }
    if (password.length < 8) {
      setLocalError('كلمة المرور يجب ألا تقل عن 8 أحرف');
      return;
    }

    try {
      const ok = await register({ email, password, username, displayName });
      if (!ok) {
        setLocalError('تعذر إنشاء الحساب. قد يكون البريد أو اسم الحساب مستخدمًا.');
        return;
      }
      setModalType('login');
      setLoginForm({ identifier: email, password: '' });
    } catch (err) {
      setLocalError('تعذر إنشاء الحساب. حاول لاحقًا.');
    }
  };

  const handleSocialLogin = (provider) => {
    const p = String(provider || '').toLowerCase();
    let url = '';

    if (p === 'google') {
      url = `${AUTH_BASE}/google/start`;
    } else if (p === 'github') {
      url = `${AUTH_BASE}/github/start`;
    } else if (p === 'microsoft') {
      url = `${AUTH_BASE}/microsoft/start`;
    }

    if (url) {
      rememberOAuthProvider(p);
      window.location.href = url;
    } else {
      console.log(`Social login for ${provider} غير مفعّل بعد`);
    }
  };



  const handleForgotEmailChange = (e) => {
    setForgotEmail(e.target.value);
    setForgotError('');
    setForgotMessage('');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    const email = String(forgotEmail || '').trim();

    if (!email) {
      setForgotError('يرجى إدخال البريد الإلكتروني');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    setForgotMessage('');

    try {
      const res = await fetch(`${AUTH_BASE}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!res.ok) {
        setForgotError('حدث خطأ أثناء إرسال الرابط. حاول مرة أخرى.');
      } else {
        setForgotMessage(
          'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك إن كان مسجلاً لدينا.'
        );
      }
    } catch (err) {
      setForgotError('تعذر الاتصال بالخادم. حاول مرة أخرى.');
    } finally {
      setForgotLoading(false);
    }
  };


  useEffect(() => {
    if (isModalOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = 'unset';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isModalOpen]);


  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    let isMounted = true;

    const fetchUnread = async () => {
      try {
        const res = await fetch(NOTIFICATIONS_UNREAD_URL, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!res.ok) {
          console.error('Failed to fetch unread notifications count', res.status);
          return;
        }

        const data = await res.json();

        const count = data.count ?? data.unreadCount;
        if (isMounted && typeof count === 'number') {
          setUnreadCount(count);
        }
      } catch (err) {
        console.error('Error fetching unread notifications count', err);
      }
    };


    fetchUnread();


    const id = setInterval(fetchUnread, 60_000);

    return () => {
      isMounted = false;
      clearInterval(id);
    };
  }, [isAuthenticated, NOTIFICATIONS_UNREAD_URL]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const renderCreateAccountContent = () => (
    <>
      <div
        className="flex flex-col items-center"
        style={{
          width: '357px',
          height: 'auto',
          gap: '22.3px',
          marginBottom: '40px'
        }}
      >
        <div
          className="flex flex-row justify-center items-center"
          style={{
            width: '357px',
            height: '29px',
            gap: '22px'
          }}
        >
          <div
            style={{
              width: '134px',
              height: '29px',
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '24px',
              lineHeight: '29px',
              textAlign: 'center',
              color: '#FFFFFF'
            }}
          >
            إنشاء حساب
          </div>
        </div>

        <div
          style={{
            width: '357px',
            height: '17px',
            fontFamily: 'Alexandria',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '17px',
            textAlign: 'center',
            color: '#D5D2D2'
          }}
        >
          انضم إلى مجتمع همة
        </div>
      </div>

      {(localError || error) && (
        <div
          className="text-red-500 text-center mb-4 px-4 py-2 rounded-lg"
          style={{
            fontFamily: 'Alexandria',
            fontSize: '14px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}
        >
          {localError || error}
        </div>
      )}

      <form onSubmit={handleRegisterSubmit} className="w-full">
        <div
          className="flex flex-col justify-center items-center"
          style={{
            width: '356.68px',
            height: 'auto',
            gap: '14.87px'
          }}
        >
          <div
            className="flex flex-row items-center rounded-xl"
            style={{
              padding: '11.1519px',
              gap: '7.43px',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="text"
              name="name"
              value={registerForm.name}
              onChange={handleRegisterChange}
              placeholder="الاسم (اختياري)"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF'
              }}
            />
          </div>

          <div
            className="flex flex-row items-center rounded-xl"
            style={{
              padding: '11.1519px',
              gap: '7.43px',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="text"
              name="accountName"
              value={registerForm.accountName}
              onChange={handleRegisterChange}
              placeholder="اسم الحساب"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF'
              }}
            />
          </div>

          <div
            className="flex flex-row items-center rounded-xl"
            style={{
              padding: '11.1519px',
              gap: '7.43px',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="email"
              name="email"
              value={registerForm.email}
              onChange={handleRegisterChange}
              placeholder="الإيميل"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF'
              }}
              required
              autoComplete="off"
            />
          </div>

          <div
            className="flex flex-row items-center rounded-xl"
            style={{
              padding: '11.1519px',
              gap: '7.43px',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="password"
              name="password"
              value={registerForm.password}
              onChange={handleRegisterChange}
              placeholder="الباسورد"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF'
              }}
              required
            />
          </div>

          <div
            className="flex flex-row items-start"
            style={{
              width: '357px',
              height: '44.3px',
              gap: '15px',
              justifyContent: 'space-between'
            }}
          >
            <button
              type="button"
              onClick={closeModal}
              className="flex flex-row justify-between items-center rounded-xl"
              style={{
                padding: '11.1519px 22.3037px',
                gap: '7.43px',
                width: '84.2px',
                height: '44.3px',
                background: '#081423',
                border: '0.929323px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  margin: '0 auto',
                  width: '37px',
                  height: '22px',
                  fontFamily: 'Alexandria',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                إغلاق
              </span>
            </button>

            <button
              type="submit"
              className="flex flex-row justify-between items-center rounded-xl"
              style={{
                padding: '11.1519px 22.3037px',
                gap: '7.43px',
                width: '251px',
                height: '44.3px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: '0.929323px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  margin: '0 auto',
                  width: '46px',
                  height: '22px',
                  fontFamily: 'Alexandria',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                تسجيل
              </span>
            </button>
          </div>
        </div>
      </form>

      <div
        className="flex flex-col items-center"
        style={{
          width: '100%',
          height: '29.74px'
        }}
      >
        <div
          style={{
            width: '100%',
            height: '0px',
            border: '0.929323px solid #FFFFFF'
          }}
        ></div>

        <div
          className="flex flex-row items-center justify-center"
          style={{
            padding: '7.43458px 0px 0px',
            gap: '4.65px',
            width: '100%',
            height: '29.43px',
            direction: 'rtl',
            paddingTop: '40px'
          }}
        >
          <span
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14.8692px',
              lineHeight: '150%',
              textAlign: 'center',
              color: '#D5D2D2'
            }}
          >
            هل لديك حساب؟
          </span>

          <button
            onClick={openLogin}
            className="underline cursor-pointer"
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14.8692px',
              lineHeight: '150%',
              textAlign: 'center',
              textDecorationLine: 'underline',
              color: '#FFFFFF'
            }}
          >
            سجل الدخول
          </button>
        </div>
      </div>
    </>
  );

  const renderLoginContent = () => (
    <>
      <div
        className="flex flex-col items-center"
        style={{
          width: '357px',
          height: 'auto',
          gap: '22.3px'
        }}
      >
        <div
          className="flex flex-row justify-center items-center"
          style={{
            width: '357px',
            height: '29px',
            gap: '22px'
          }}
        >
          <div
            style={{
              width: '100%',
              height: '29px',
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '24px',
              lineHeight: '29px',
              textAlign: 'center',
              color: '#FFFFFF'
            }}
          >
            تسجيل الدخول
          </div>
        </div>

        <div
          style={{
            width: '357px',
            height: '17px',
            fontFamily: 'Alexandria',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '17px',
            textAlign: 'center',
            color: '#D5D2D2'
          }}
        >
          مرحبا بك
        </div>
      </div>

      {(error || localError) && (
        <div
          className="text-red-500 text-center mb-4 px-4 py-2 rounded-lg"
          style={{
            fontFamily: 'Alexandria',
            fontSize: '14px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}
        >
          {error || localError}
        </div>
      )}

      <form onSubmit={handleLoginSubmit} className="w-full">
        <div
          className="flex flex-col justify-center items-center"
          style={{
            width: '356.68px',
            height: 'auto',
            gap: '14.87px'
          }}
        >
          <div
            className="flex flex-row items-center rounded-xl"
            style={{
              padding: '11.1519px',
              gap: '7.43px',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="text"
              name="identifier"
              value={loginForm.identifier}
              onChange={handleInputChange}
              placeholder="الايميل أو اسم المستخدم"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF',
                backgroundColor: 'transparent !important',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: '#FFFFFF'
              }}
              required
              autoComplete="off"
            />
          </div>

          <div
            className="flex flex-row items-center rounded-xl"
            style={{
              padding: '11.1519px',
              gap: '7.43px',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="password"
              name="password"
              value={loginForm.password}
              onChange={handleInputChange}
              placeholder="كلمة السر"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF',
                backgroundColor: 'transparent !important',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: '#FFFFFF'
              }}
              required
            />
          </div>

          <div
            className="flex flex-row justify-center items-center"
            style={{
              width: '356.86px',
              height: '44.3px',
              marginTop: '10px'
            }}
          >
            <button
              type="submit"
              className="flex flex-row justify-center items-center rounded-xl w-full"
              style={{
                padding: '11.1519px 22.3037px',
                height: '44.3px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: '0.929323px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  fontFamily: 'Alexandria',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                تسجيل الدخول
              </span>
            </button>
          </div>
        </div>
      </form>

      <div
        className="flex flex-col items-center"
        style={{
          width: '100%',
          height: 'auto'
        }}
      >
        <div
          style={{
            width: '100%',
            height: '0px',
            border: '0.929323px solid #FFFFFF'
          }}
        ></div>

        <div
          className="flex flex-col items-center"
          style={{
            width: '100%',
            gap: '14.87px',
            paddingTop: '20px'
          }}
        >
          <button
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '11.1519px',
              justifyContent: 'space-between',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
            type="button"
            onClick={() => handleSocialLogin('google')}
          >
            <svg
              width="19"
              height="18"
              viewBox="0 0 19 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.25 8.84268C16.25 11.9552 14.5925 15.7502 9.5975 15.7502C5.84346 15.7751 2.77889 12.7542 2.75 9.00018C2.77889 5.24614 5.84346 2.22521 9.5975 2.25015C11.1505 2.25591 12.6564 2.78428 13.8725 3.75015C13.9565 3.8188 14.0082 3.91936 14.015 4.02765C14.0155 4.13722 13.9723 4.24246 13.895 4.32015C13.4068 4.76655 12.9411 5.23714 12.5 5.73015C12.3717 5.87138 12.1567 5.89092 12.005 5.77515C11.3121 5.26236 10.4666 4.99814 9.605 5.02515C7.38896 5.02515 5.5925 6.82161 5.5925 9.03768C5.5925 11.2537 7.38896 13.0502 9.605 13.0502C11.855 13.0502 12.8075 12.0902 13.3025 10.3877H9.875C9.66792 10.3877 9.5 10.2198 9.5 10.0127V8.02518C9.5 7.81803 9.66792 7.65018 9.875 7.65018H15.875C16.0476 7.64906 16.1933 7.77851 16.2125 7.95018C16.2403 8.24681 16.2529 8.54471 16.25 8.84268Z"
                fill="white"
              />
            </svg>
            <span
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
            >
              سجل الدخول بواسطة Google
            </span>
          </button>

          <button
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '11.1519px',
              justifyContent: 'space-between',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
            type="button"
            onClick={() => handleSocialLogin('microsoft')}
          >
            <svg
              width="19"
              height="18"
              viewBox="0 0 19 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="2" y="2" width="6" height="6" fill="white" />
              <rect x="11" y="2" width="6" height="6" fill="white" />
              <rect x="2" y="10" width="6" height="6" fill="white" />
              <rect x="11" y="10" width="6" height="6" fill="white" />
            </svg>
            <span
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
            >
              سجل الدخول بواسطة Microsoft
            </span>
          </button>

          <button
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '11.1519px',
              justifyContent: 'space-between',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
            type="button"
            onClick={() => handleSocialLogin('github')}
          >
            <svg
              width="19"
              height="18"
              viewBox="0 0 19 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.5 1C5.358 1 2 4.358 2 8.5C2 12.142 4.357 15.243 7.615 16.29C8.003 16.357 8.139 16.122 8.139 15.92C8.139 15.743 8.132 15.26 8.129 14.62C5.949 15.096 5.487 13.626 5.487 13.626C5.137 12.733 4.606 12.496 4.606 12.496C3.861 12.986 4.662 12.975 4.662 12.975C5.487 13.034 5.923 13.826 5.923 13.826C6.655 15.083 7.863 14.712 8.333 14.51C8.405 13.987 8.609 13.64 8.834 13.445C7.115 13.247 5.301 12.555 5.301 9.758C5.301 8.97 5.582 8.33 6.049 7.833C5.972 7.635 5.728 6.915 6.119 5.911C6.119 5.911 6.713 5.709 8.126 6.64C8.691 6.483 9.298 6.405 9.903 6.402C10.508 6.405 11.115 6.483 11.681 6.64C13.092 5.709 13.685 5.911 13.685 5.911C14.077 6.915 13.833 7.635 13.756 7.833C14.224 8.33 14.503 8.97 14.503 9.758C14.503 12.563 12.685 13.244 10.961 13.438C11.247 13.684 11.501 14.155 11.501 14.877C11.501 15.905 11.492 16.697 11.492 15.92C11.492 16.122 11.626 16.359 12.02 16.288C15.279 15.241 17.636 12.142 17.636 8.5C17.636 4.358 14.278 1 10.136 1H9.5Z"
                fill="white"
              />
            </svg>
            <span
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
            >
              سجل الدخول بواسطة GitHub
            </span>
          </button>
        </div>

        <div
          className="flex flex-col items-center"
          style={{
            width: '100%',
            gap: '10px',
            paddingTop: '20px'
          }}
        >
          <button
            onClick={openForgotPassword}
            className="underline cursor-pointer"
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '150%',
              textAlign: 'center',
              textDecorationLine: 'underline',
              color: '#FFFFFF'
            }}
            type="button"
          >
            هل نسيت كلمة المرور؟
          </button>

          <div
            className="flex flex-row items-center justify-center"
            style={{
              gap: '4.65px',
              direction: 'rtl'
            }}
          >
            <span
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '150%',
                textAlign: 'center',
                color: '#D5D2D2'
              }}
            >
              ليس لديك حساب؟
            </span>
            <button
              onClick={openCreateAccount}
              className="underline cursor-pointer"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '150%',
                textAlign: 'center',
                textDecorationLine: 'underline',
                color: '#FFFFFF'
              }}
              type="button"
            >
              سجل حساب جديد
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const renderForgotPasswordContent = () => (
    <>
      <div
        className="flex flex-col items-center"
        style={{
          width: '357px',
          height: 'auto',
          gap: '12px',
          marginBottom: '12px'
        }}
      >
        <div
          className="flex flex-row justify-center items-center"
          style={{
            width: '357px',
            height: '29px',
            gap: '22px'
          }}
        >
          <div
            style={{
              width: '100%',
              height: '29px',
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '24px',
              lineHeight: '29px',
              textAlign: 'center',
              color: '#FFFFFF'
            }}
          >
            استرجاع كلمة المرور
          </div>
        </div>

        <div
          style={{
            width: '357px',
            minHeight: '34px',
            fontFamily: 'Alexandria',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '13px',
            lineHeight: '19px',
            textAlign: 'center',
            color: '#D5D2D2'
          }}
        >
          أدخل البريد الإلكتروني المرتبط بحسابك، وسنرسل لك رابط إعادة تعيين كلمة
          المرور إن كان مسجلاً لدينا.
        </div>
      </div>

      {forgotError && (
        <div
          className="text-red-500 text-center mb-2 px-4 py-2 rounded-lg"
          style={{
            fontFamily: 'Alexandria',
            fontSize: '14px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}
        >
          {forgotError}
        </div>
      )}

      {forgotMessage && (
        <div
          className="text-green-500 text-center mb-2 px-4 py-2 rounded-lg"
          style={{
            fontFamily: 'Alexandria',
            fontSize: '14px',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)'
          }}
        >
          {forgotMessage}
        </div>
      )}

      <form onSubmit={handleForgotSubmit} className="w-full" dir="rtl">
        <div
          className="flex flex-col justify-center items-center"
          style={{
            width: '356.86px',
            height: 'auto',
            gap: '14.87px'
          }}
        >
          <div
            className="flex flex-row items-center rounded-xl"
            style={{
              padding: '11.1519px',
              gap: '7.43px',
              width: '356.86px',
              height: '38.3px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '0.929323px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="email"
              value={forgotEmail}
              onChange={handleForgotEmailChange}
              placeholder="البريد الإلكتروني"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF'
              }}
              autoComplete="off"
            />
          </div>

          <div
            className="flex flex-row items-start"
            style={{
              width: '357px',
              height: '44.3px',
              gap: '15px',
              justifyContent: 'space-between',
              marginTop: '8px'
            }}
          >
            <button
              type="button"
              onClick={closeModal}
              className="flex flex-row justify-between items-center rounded-xl"
              style={{
                padding: '11.1519px 22.3037px',
                gap: '7.43px',
                width: '84.2px',
                height: '44.3px',
                background: '#081423',
                border: '0.929323px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  margin: '0 auto',
                  width: '37px',
                  height: '22px',
                  fontFamily: 'Alexandria',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                إغلاق
              </span>
            </button>

            <button
              type="submit"
              disabled={forgotLoading}
              className="flex flex-row justify-between items-center rounded-xl disabled:opacity-60"
              style={{
                padding: '11.1519px 22.3037px',
                gap: '7.43px',
                width: '251px',
                height: '44.3px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: '0.929323px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  margin: '0 auto',
                  width: 'auto',
                  height: '22px',
                  fontFamily: 'Alexandria',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                {forgotLoading ? 'جارٍ الإرسال...' : 'إرسال رابط إعادة التعيين'}
              </span>
            </button>
          </div>
        </div>
      </form>

      <div
        className="flex flex-col items-center"
        style={{
          width: '100%',
          marginTop: '20px'
        }}
      >
        <div
          style={{
            width: '100%',
            height: '0px',
            border: '0.929323px solid #FFFFFF'
          }}
        ></div>

        <div
          className="flex flex-col items-center"
          style={{
            width: '100%',
            gap: '8px',
            paddingTop: '18px'
          }}
        >
          <button
            type="button"
            onClick={openLogin}
            className="underline cursor-pointer"
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '150%',
              textAlign: 'center',
              textDecorationLine: 'underline',
              color: '#FFFFFF'
            }}
          >
            رجوع إلى تسجيل الدخول
          </button>

          <div
            className="flex flex-row items-center justify-center"
            style={{
              gap: '4.65px',
              direction: 'rtl'
            }}
          >
            <span
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '150%',
                textAlign: 'center',
                color: '#D5D2D2'
              }}
            >
              ليس لديك حساب؟
            </span>
            <button
              type="button"
              onClick={openCreateAccount}
              className="underline cursor-pointer"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '150%',
                textAlign: 'center',
                textDecorationLine: 'underline',
                color: '#FFFFFF'
              }}
            >
              سجل حساب جديد
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <header
        className="hidden md:flex items-center px-6 py-4 text-white fixed h-[70px] z-[300]"
        style={{
          background: 'transparent',
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          top: '40px',
          left: '90px',
          right: '0',
          position: 'relative'
        }}
      >
        <div className="flex items-center gap-4">
          {isAuthenticated ? (
            <>
              <HeaderProfileIcon onClick={() => navigate('/profile')} />
              <NotificationIcon
                size="desktop"
                unreadCount={unreadCount}
                onClick={() => setOpenNotif((v) => !v)}
              />

              <NotificationsPanel
                isOpen={openNotif}
                onClose={() => setOpenNotif(false)}
                onUnreadCountChange={setUnreadCount}
              />
            </>
          ) : (
            <>
              <button
                onClick={openCreateAccount}
                className="flex items-center justify-center px-[35px] py-[10px] gap-2 w-[159px] h-[50px] text-white font-medium text-base leading-5 text-right rounded-xl cursor-pointer"
                style={{
                  background:
                    'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                  boxShadow:
                    'inset 0px -1px 0px rgba(255, 255, 255, 0.25), inset 0px 1px 0px rgba(255, 255, 255, 0.25)',
                  fontFamily: 'Alexandria',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '20px'
                }}
              >
                إنشاء حساب
              </button>

              <button
                onClick={openLogin}
                className="text-white font-medium text-base cursor-pointer"
              >
                تسجيل الدخول
              </button>
            </>
          )}
        </div>
      </header>

      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{
              top: window.scrollY,
              height: '100vh',
              position: 'fixed'
            }}
          >
            <div
              className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm cursor-pointer"
              onClick={handleBackdropClick}
            ></div>

            <div
              className="relative flex flex-col justify-start items-center rounded-xl z-10"
              style={{
                width: '407px',
                height: '593px',
                padding: '44.6075px 25px',
                gap: '29.74px',
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                borderRadius: '12px',
                boxShadow: '0px 8px 32px 0px rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(2px)',
                WebkitBackdropFilter: 'blur(2px)'
              }}
            >
              {modalType === 'create'
                ? renderCreateAccountContent()
                : modalType === 'login'
                ? renderLoginContent()
                : renderForgotPasswordContent()}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default DesktopHeader;
